import { useState, useMemo } from 'react';
import { getEstadoColor, getSubEstadoColor } from '../constants/reservaColors';

/**
 * Hook personalizado para manejar filtros de reservas por estado y sub-estado
 * Los estados y sub-estados están vinculados por color
 * 
 * @param {Object} initialEstados - Estados iniciales activos
 * @param {Object} initialSubEstados - Sub-estados iniciales activos
 * @param {Array} reservas - Array de reservas a filtrar
 * @returns {Object} - Objeto con estados, funciones toggle y reservas filtradas
 */
export const useReservaFilters = (initialEstados = {}, initialSubEstados = {}, reservas = []) => {
  const [estadosActivos, setEstadosActivos] = useState(initialEstados);
  const [subEstadosActivos, setSubEstadosActivos] = useState(initialSubEstados);

  const toggleEstado = (estado) => {
    const nuevoEstado = !estadosActivos[estado];
    const colorEstado = getEstadoColor(estado);

    setEstadosActivos(prev => ({
      ...prev,
      [estado]: nuevoEstado
    }));

    // Activar/desactivar todos los sub-estados del mismo color
    setSubEstadosActivos(prev => {
      const nuevosSubEstados = { ...prev };
      Object.keys(nuevosSubEstados).forEach(subEstado => {
        if (getSubEstadoColor(subEstado) === colorEstado) {
          nuevosSubEstados[subEstado] = nuevoEstado;
        }
      });
      return nuevosSubEstados;
    });
  };

  const toggleSubEstado = (subEstado) => {
    const nuevoSubEstado = !subEstadosActivos[subEstado];
    const colorSubEstado = getSubEstadoColor(subEstado);

    setSubEstadosActivos(prev => ({
      ...prev,
      [subEstado]: nuevoSubEstado
    }));

    // Si activamos un sub-estado, activar también el estado del mismo color
    if (nuevoSubEstado) {
      setEstadosActivos(prev => {
        const nuevosEstados = { ...prev };
        Object.keys(nuevosEstados).forEach(estado => {
          if (getEstadoColor(estado) === colorSubEstado) {
            nuevosEstados[estado] = true;
          }
        });
        return nuevosEstados;
      });
    }
  };

  const reservasFiltradas = useMemo(() => {
    if (!Array.isArray(reservas)) return [];

    return reservas.filter(reserva => {
      const estadoActivo = estadosActivos[reserva.estado];
      const subEstadoActivo = subEstadosActivos[reserva.sub_estado];

      // Si el estado no está en el objeto, se considera activo por defecto
      const pasaFiltroEstado = estadoActivo === undefined ? true : estadoActivo;
      const pasaFiltroSubEstado = subEstadoActivo === undefined ? true : subEstadoActivo;

      return pasaFiltroEstado && pasaFiltroSubEstado;
    });
  }, [reservas, estadosActivos, subEstadosActivos]);

  return {
    estadosActivos,
    subEstadosActivos,
    toggleEstado,
    toggleSubEstado,
    reservasFiltradas,
    setEstadosActivos,
    setSubEstadosActivos
  };
};
